/**
 * Call-ins at L1 on turns (IDEAS X11): a person brings the big gun. A unit, bot or held, asks the command layer for
 * fire on a point it can see; the commander's bot answers with a fire mission (lane B's `fireMission` task, §15.2)
 * for a gun that can reach it — a living teammate on another planet with ammunition, since a fire mission shells
 * across planets — or refuses with the reason. Every ask and its answer is logged on the state (`callIns`), so the
 * panel and the log can say so. Behind `GameConfig.callIns`. Pure; deterministic.
 */
import { WEAPONS } from '../constants';
import { dhypot } from './dmath';
import type { GameState, PlayerState, SquadOrder, Vec3 } from '../types';
import { squadOf } from './squads';

export type CallInOrder = Extract<SquadOrder, { kind: 'callIn' }>;

/** A squad may call in once every this many turns. */
export const CALL_IN_COOLDOWN_TURNS = 2;
/** The spread a called-in mission is fired with: the asker's own eyes make the tightest fix. */
export const CALL_IN_SPREAD = 0;

export interface CallInRecord {
  turn: number;
  by: number;
  team: number;
  planetIndex: number;
  surfaceDir: Vec3;
  answer: 'fired' | 'refused';
  /** Who fires it, or why not. */
  detail: string;
  gun?: number;
}

/** Any weapon the unit can still fire (a null count is unlimited). */
export function hasAnyAmmo(p: PlayerState): boolean {
  return Object.values(p.loadout.ammo).some(n => n === null || n === undefined || n > 0);
}

/**
 * The gun that would answer: the nearest living teammate on another planet than the point, with ammunition, not the
 * asker. Null when nobody can reach.
 */
export function gunFor(state: Pick<GameState, 'players' | 'planets'>, asker: PlayerState, planetIndex: number): PlayerState | null {
  const guns = state.players.filter(p => p.alive && p.teamIndex === asker.teamIndex && p.id !== asker.id && p.planetIndex !== planetIndex && hasAnyAmmo(p));
  if (!guns.length) return null;
  const target = state.planets[planetIndex];
  const d2 = (p: PlayerState) => { const q = state.planets[p.planetIndex]; const dx = q.position.x - target.position.x, dy = q.position.y - target.position.y, dz = q.position.z - target.position.z; return dx * dx + dy * dy + dz * dz; };
  return guns.reduce((a, p) => (d2(p) < d2(a) || (d2(p) === d2(a) && p.id < a.id) ? p : a));
}

/** Why a squad may not call in now, or null. */
export function callInBlocked(state: Pick<GameState, 'players' | 'planets' | 'squads' | 'callIns' | 'turnNumber'>, asker: PlayerState, planetIndex: number): string | null {
  if (!state.planets[planetIndex]?.alive) return 'no such planet to fire on';
  const sq = squadOf(state, asker.id);
  if (!sq) return 'the asker is in no squad';
  const last = [...(state.callIns ?? [])].reverse().find(c => c.answer === 'fired' && c.team === asker.teamIndex && squadOf(state, c.by)?.id === sq.id);
  if (last && state.turnNumber - last.turn < CALL_IN_COOLDOWN_TURNS) return `the guns are busy: ${sq.id === squadOf(state, last.by)?.id ? 'this squad' : 'a squad'} called in on turn ${last.turn}`;
  if (!gunFor(state, asker, planetIndex)) return 'no gun off that planet with ammunition';
  return null;
}

/**
 * The ask a bot makes on its turn: the nearest enemy it can see on its own planet — where its own launcher is the
 * wrong tool and a gun elsewhere can shell — when a gun can answer and the squad is not on cooldown.
 */
export function wantsCallIn(state: Pick<GameState, 'players' | 'planets' | 'squads' | 'callIns' | 'turnNumber' | 'config'>, unit: PlayerState, visibleEnemyIds: readonly number[], pictureTargets: ReadonlyArray<{ planetIndex: number; surfaceDir: Vec3 }> = []): { planetIndex: number; surfaceDir: Vec3 } | null {
  // asked when the unit's own fire search found no shot (lane B's every-switch check, 62.43): the nearest thing the
  // team knows of — an enemy in sight, else a picture target — goes to the guns on another planet
  if (!state.config.callIns) return null;
  const known: Array<{ planetIndex: number; surfaceDir: Vec3 }> = [
    ...state.players.filter(p => visibleEnemyIds.includes(p.id) && p.alive && p.teamIndex !== unit.teamIndex).map(p => ({ planetIndex: p.planetIndex, surfaceDir: p.surfaceDir })),
    ...pictureTargets,
  ];
  let best: { planetIndex: number; surfaceDir: Vec3 } | null = null; let bd = Infinity;
  for (const t of known) {
    if (callInBlocked(state, unit, t.planetIndex)) continue;
    const d = distanceTo(state, unit, t);
    if (d < bd) { bd = d; best = t; }
  }
  return best ? { planetIndex: best.planetIndex, surfaceDir: best.surfaceDir } : null;
}

/**
 * Danger close (62.43): an enemy in sight on the unit's own planet, nearer than its own shells can land without catching
 * it (twice the largest blast it carries, at least 4 u) — beyond its weapons' reach, so it asks the guns elsewhere.
 */
export function dangerClose(state: Pick<GameState, 'players' | 'planets' | 'squads' | 'callIns' | 'turnNumber' | 'config'>, unit: PlayerState, visibleEnemyIds: readonly number[], blasts = 2): { planetIndex: number; surfaceDir: Vec3 } | null {
  if (!state.config.callIns) return null;
  const blast = Math.max(2, ...Object.entries(unit.loadout?.ammo ?? {}).filter(([, n]) => n === null || (n as number) > 0).map(([k]) => WEAPONS[k as keyof typeof WEAPONS]?.blastRadius ?? 0));
  const near = state.players.filter(p => visibleEnemyIds.includes(p.id) && p.alive && p.teamIndex !== unit.teamIndex && p.planetIndex === unit.planetIndex && distanceTo(state, unit, p) < blasts * blast);   // `blasts`: the bot's tuning (asks.dangerCloseBlasts)
  if (!near.length) return null;
  const t = near.reduce((a, p) => (distanceTo(state, unit, p) < distanceTo(state, unit, a) ? p : a));
  return callInBlocked(state, unit, t.planetIndex) ? null : { planetIndex: t.planetIndex, surfaceDir: t.surfaceDir };
}

/** Straight-line distance from a unit to a point on a planet's surface (for choosing the nearest known target). */
function distanceTo(state: Pick<GameState, 'planets'>, unit: PlayerState, t: { planetIndex: number; surfaceDir: Vec3 }): number {
  const a = state.planets[unit.planetIndex], b = state.planets[t.planetIndex];
  if (!a || !b) return Infinity;
  const pa = { x: a.position.x + unit.surfaceDir.x * a.radius, y: a.position.y + unit.surfaceDir.y * a.radius, z: a.position.z + unit.surfaceDir.z * a.radius };
  const pb = { x: b.position.x + t.surfaceDir.x * b.radius, y: b.position.y + t.surfaceDir.y * b.radius, z: b.position.z + t.surfaceDir.z * b.radius };
  return dhypot(pa.x - pb.x, pa.y - pb.y, pa.z - pb.z);
}

/**
 * The commander's fire mission (lane B's check, 62.43): a picture target on a planet where the team has nobody — no unit
 * there can reach it — goes to the nearest gun of the team (a living unit with rounds, on another planet) as a fire
 * mission. One outstanding mission per team: none while one is still unfired. The most confident target first.
 */
export function commanderMission(state: Pick<GameState, 'players' | 'planets' | 'squads' | 'config'>, team: number, pictureTargets: ReadonlyArray<{ planetIndex: number; surfaceDir: Vec3; confidence?: number }>): { gunId: number; planetIndex: number; surfaceDir: Vec3 } | null {
  if (!state.config.callIns || !state.squads?.length) return null;
  const mine = state.players.filter(p => p.teamIndex === team && p.alive);
  if (state.squads.some(sq => sq.team === team && sq.tasks.some(t => t.order.kind === 'fireMission'))) return null;
  const occupied = new Set(mine.map(p => p.planetIndex));
  const targets = pictureTargets.filter(t => !occupied.has(t.planetIndex) && state.planets[t.planetIndex]?.alive)
    .slice().sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
  for (const t of targets) {
    let gun: PlayerState | null = null; let gd = Infinity;
    for (const p of mine) {
      if (p.planetIndex === t.planetIndex || !hasAnyAmmo(p)) continue;
      const d = distanceTo(state, p, t);
      if (d < gd) { gd = d; gun = p; }
    }
    if (gun) return { gunId: gun.id, planetIndex: t.planetIndex, surfaceDir: t.surfaceDir };
  }
  return null;
}

/** The log line for a record. */
export function callInText(state: Pick<GameState, 'players'>, c: CallInRecord): string {
  const by = state.players.find(p => p.id === c.by)?.name ?? `unit ${c.by}`;
  return c.answer === 'fired' ? `${by} calls in fire: ${c.detail}` : `${by} calls in fire: refused — ${c.detail}`;
}
